/**
 * ServiceDetailPage — single dynamic detail page for each operated service
 * (`japanetccard`, `japantollreceipts`, `japanchangemoney`).
 *
 * The /services hub stays as the high-level overview. This page is the
 * deep-dive: pulls everything from SERVICE_CONTENT *plus* a live snapshot
 * of the service's most recent changelog releases and active roadmap items
 * from the Mongo-first federation feed, plus the service's support
 * contact information.
 *
 * Routing: /:lang/services/:slug → this component (slug ∈ SERVICE_CONTENT)
 */
import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Info,
  Mail,
  MapPinned,
  Package,
  ShieldCheck,
  Sparkles,
  Tag,
  Users,
} from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { serviceSchema, breadcrumbSchema, faqSchema } from "@/components/JsonLd";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import AppTooltip from "@/components/ui-system/AppTooltip";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";
import ServiceJumpNav from "@/components/ServiceJumpNav";
import ServiceTeamBand from "@/components/ServiceTeamBand";
import TestimonialRotator from "@/components/TestimonialRotator";
import { withLang } from "@/lib/externalLink";
import { SERVICE_CONTENT, SERVICE_NARRATIVE } from "@/data/serviceContent";
import { IMG } from "@/data/imageLibrary";
import { JEC_TEAM, JEC_TEAM_GROUP_PHOTOS, JEC_TEAM_MANIFESTO } from "@/data/jecTeam";
import { JTR_TEAM, JTR_TEAM_GROUP_PHOTOS, JTR_TEAM_MANIFESTO } from "@/data/jtrTeam";
import ServiceVisualsBoard from "@/components/ServiceVisualsBoard";
import SignboardsShowcase from "@/components/SignboardsShowcase";
import { useEcosystemDataset } from "@/lib/useEcosystemData";
import { formatPublicDate } from "@/lib/formatPublicDate";
import { t } from "@/lib/i18n";

const WORDMARK_MAP = {
  japanetccard: IMG.japanetccardWordmark,
  japantollreceipts: IMG.japantollreceiptsWordmark,
  japanchangemoney: IMG.japanchangemoneyWordmark,
};

const EDITORIAL_IMG = {
  japanetccard: IMG.detailEtcLane,
};

const SAMPLE_CARD = {
  japanetccard: IMG.japanetccardSample,
};

const SOFT_BG = {
  "#5B2E83": "#F4EEF9",
  "#2F8F3C": "#ECF6EC",
  "#0E5FB0": "#E8F0FA",
};

const SLUG_TO_FRESHNESS = (sourceFreshness, slug) =>
  (sourceFreshness || []).filter((s) => s.serviceId === slug);

function pickLang(field, lang) {
  if (!field) return "";
  if (typeof field === "string") return field;
  return field[lang] || field.en || field.ja || "";
}

function SectionEyebrow({ accent, children }) {
  return (
    <div className="inline-flex items-center gap-2 mb-3">
      <span aria-hidden className="h-1.5 w-1.5" style={{ backgroundColor: accent }} />
      <span
        className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold"
        style={{ color: accent }}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * ReceiptsBrandPanel — stylized "inbox with toll reports" art for the JTR
 * hero. Pure CSS / SVG so it never serves an off-brand stock photo. The
 * design intentionally evokes the JTR daily/weekly/monthly PDF reports
 * landing in an inbox.
 */
function ReceiptsBrandPanel({ accent, isJa }) {
  return (
    <div
      className="relative aspect-[5/4] w-full rounded-2xl overflow-hidden p-8 flex items-center justify-center"
      style={{
        background:
          "linear-gradient(135deg, #FFFFFF 0%, #ECF6EC 60%, #DCEFDC 100%)",
        boxShadow: "0 25px 55px -25px rgba(15,29,45,0.35)",
      }}
      data-testid="service-detail-japantollreceipts-art"
    >
      <BrandGraphic
        variant="wave"
        color={accent}
        className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none"
      />
      {/* No corner-anchor gate here — the brand identity is already shown
          in the hero's wordmark+icon lockup. The panel focuses on the
          three layered report cards (daily / weekly / monthly). */}
      <div className="relative w-full max-w-[340px]">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute left-0 right-0 rounded-xl bg-white ring-1 ring-[#E2E8F0] shadow-[0_10px_30px_-15px_rgba(15,29,45,0.35)] p-4"
            style={{
              top: `${i * 26}px`,
              transform: `translateX(${i === 1 ? 12 : i === 2 ? 0 : -8}px) rotate(${
                i === 0 ? -2 : i === 1 ? 1.2 : -0.5
              }deg)`,
              zIndex: 3 - i,
            }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <div
                className="text-[9.5px] tracking-[0.22em] font-mono uppercase font-bold"
                style={{ color: accent }}
              >
                {i === 0
                  ? isJa ? "日次ETCレポート" : "Daily ETC report"
                  : i === 1
                  ? isJa ? "週次サマリー" : "Weekly summary"
                  : isJa ? "月次レポート" : "Monthly report"}
              </div>
              <div className="text-[9px] text-[#94A3B8] font-mono">
                {i === 0 ? "05-18" : i === 1 ? "05-12" : "04-30"}
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="h-1.5 w-full bg-slate-100 rounded-full" />
              <div className="h-1.5 w-4/5 bg-slate-100 rounded-full" />
              <div className="h-1.5 w-3/5 bg-slate-100 rounded-full" />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span
                className="text-[8.5px] tracking-[0.18em] font-mono uppercase font-bold px-1.5 py-0.5 rounded"
                style={{ color: accent, backgroundColor: `${accent}15` }}
              >
                PDF + CSV
              </span>
              <span className="text-[10px] font-mono text-[#0F1D2D] font-bold">
                ¥{(2400 + i * 1280).toLocaleString()}
              </span>
            </div>
          </div>
        ))}
        {/* Spacer to give the absolutely-positioned cards height */}
        <div className="invisible">
          <div className="rounded-xl p-4">
            <div className="h-3 mb-2.5" />
            <div className="space-y-1.5">
              <div className="h-1.5" />
              <div className="h-1.5" />
              <div className="h-1.5" />
            </div>
            <div className="mt-3 h-3" />
          </div>
        </div>
        <div className="invisible h-[52px]" />
      </div>
    </div>
  );
}

/**
 * ExchangeBrandPanel — stylized "currency exchange ticker" art for the
 * JapanChangeMoney hero. Pure CSS — no stock photo, no fake "operating
 * exchange counter" misrepresentation. Communicates the brand cleanly
 * without claiming the service is live.
 */
function ExchangeBrandPanel({ accent, isJa }) {
  // Service plans to support USD ↔ JPY only at launch (owner directive
  // 2026-05-21). Ticker preview reflects that scope — no other pairs.
  const rows = [
    { code: "USD", val: "152.43", delta: "+0.18", up: true },
  ];
  return (
    <div
      className="relative aspect-[5/4] w-full rounded-2xl overflow-hidden p-7 md:p-9 flex flex-col justify-between"
      style={{
        background:
          "linear-gradient(135deg, #FFFFFF 0%, #E8F0FA 55%, #D0E1F4 100%)",
        boxShadow: "0 25px 55px -25px rgba(15,29,45,0.35)",
      }}
      data-testid="service-detail-japanchangemoney-art"
    >
      <BrandGraphic
        variant="wave"
        color={accent}
        className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none"
      />
      <div className="relative">
        <div
          className="inline-flex items-center gap-1.5 text-[9.5px] tracking-[0.24em] font-mono uppercase font-bold px-2 py-0.5 rounded-full border"
          style={{ color: accent, borderColor: `${accent}55` }}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
          {isJa ? "予定レート（プレビュー）" : "Planned rates (preview)"}
        </div>
        <div className="mt-3 text-[11px] tracking-[0.18em] font-mono uppercase font-bold text-[#0F1D2D]">
          {isJa ? "対 日本円" : "vs. Japanese Yen (JPY)"}
        </div>
      </div>

      <div className="relative grid grid-cols-1 gap-1.5">
        {rows.map((r) => (
          <div
            key={r.code}
            className="flex items-center justify-between bg-white/85 backdrop-blur-sm rounded-lg ring-1 ring-white/60 px-3 py-2"
          >
            <span
              className="text-[12px] font-bold tracking-tight font-mono"
              style={{ color: accent }}
            >
              {r.code}
              <span className="text-[#94A3B8] mx-1 font-normal">/</span>
              <span className="text-[#0F1D2D]">JPY</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-[13px] font-bold font-mono text-[#0F1D2D]">¥{r.val}</span>
              <span
                className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                  r.up ? "text-[#2F8F3C] bg-[#ECF6EC]" : "text-[#DC2626] bg-[#FEE2E2]"
                }`}
              >
                {r.delta}
              </span>
            </span>
          </div>
        ))}
      </div>

      <div className="relative text-[9.5px] tracking-[0.18em] font-mono uppercase text-[#64748B]">
        {isJa
          ? "※ 表示は予定。実際の両替は未開始です。"
          : "Illustrative — service not yet conducting exchange."}
      </div>
    </div>
  );
}

function NotFound({ lang }) {
  const isJa = lang === "ja";
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-white px-6">
      <div className="text-center max-w-md">
        <p className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold mb-3">
          {isJa ? "ページが見つかりません" : "Service not found"}
        </p>
        <h1 className="text-2xl font-bold text-[#0F1D2D] mb-3">
          {isJa ? "サービス詳細が見つかりませんでした。" : "We couldn't find that service detail page."}
        </h1>
        <Link
          to={`/${lang}/services`}
          className="inline-flex items-center gap-2 text-[#0E5FB0] font-semibold hover:underline"
        >
          {isJa ? "サービス一覧へ戻る" : "Back to all services"}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

export default function ServiceDetailPage({ lang }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = SERVICE_CONTENT[slug];
  const narrative = SERVICE_NARRATIVE[slug];
  const dataset = useEcosystemDataset(lang);
  const isJa = lang === "ja";

  // Filter live datasets to this service.
  const serviceReleases = useMemo(
    () => (dataset.RELEASES || []).filter((r) => r._serviceId === slug).slice(0, 5),
    [dataset.RELEASES, slug],
  );
  const serviceMilestones = useMemo(() => {
    const all = dataset.MILESTONES || [];
    // Prioritize in_progress > planned > in_testing > launch > completed; cap at 6.
    const order = { in_progress: 0, in_testing: 1, planned: 2, launch: 3, completed: 4 };
    return all
      .filter((m) => m._serviceId === slug)
      .sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9))
      .slice(0, 6);
  }, [dataset.MILESTONES, slug]);

  const serviceFreshness = useMemo(
    () => SLUG_TO_FRESHNESS(dataset.sourceFreshness, slug),
    [dataset.sourceFreshness, slug],
  );

  if (!data) return <NotFound lang={lang} />;

  const isPlanned = slug === "japanchangemoney";
  const accent = data.accent;
  const soft = SOFT_BG[accent] || data.accentSoft || "#F4EEF9";

  const seoTitle = isJa
    ? `${data.title.ja} | NoJo Enterprise`
    : `${data.title.en} | NoJo Enterprise`;
  const seoDesc = pickLang(data.intro, lang).slice(0, 160);
  const seoKey =
    slug === "japanetccard"
      ? "servicesJEC"
      : slug === "japantollreceipts"
      ? "servicesJTR"
      : slug === "japanchangemoney"
      ? "servicesJCM"
      : null;
  const seoEntry = seoKey ? PAGE_SEO[seoKey][lang] : null;

  return (
    <article className="bg-white" data-testid={`service-detail-${slug}`}>
      <SEO
        title={seoEntry?.title || seoTitle}
        description={seoEntry?.description || seoDesc}
        keywords={seoEntry?.keywords}
        path={`/${lang}/services/${slug}`}
        lang={lang}
        image={
          slug === "japanetccard"
            ? "/social/nojo-og-jec.png"
            : slug === "japantollreceipts"
              ? "/social/nojo-og-jtr.png"
              : slug === "japanchangemoney"
                ? "/social/nojo-og-jcm.png"
                : undefined
        }
        imageAlt={
          slug === "japanetccard"
            ? "JapanETCcard — ETC card support for foreign residents in Japan"
            : slug === "japantollreceipts"
              ? "JapanTollReceipts — Automated ETC toll records in your inbox"
              : slug === "japanchangemoney"
                ? "JapanChangeMoney — Currency exchange logistics, planned launch 2027"
                : undefined
        }
      />
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: lang === "ja" ? "ホーム" : "Home", url: `/${lang}` },
            { name: lang === "ja" ? "サービス" : "Services", url: `/${lang}/services` },
            { name: data.domain, url: `/${lang}/services/${slug}` },
          ]),
          serviceSchema({
            name: data.domain,
            description: seoEntry?.description || seoDesc,
            url: `/${lang}/services/${slug}`,
            serviceType:
              slug === "japanetccard"
                ? "ETC card support service"
                : slug === "japantollreceipts"
                ? "Automated toll-record reporting"
                : "Currency exchange service",
            brand: data.domain,
            inLanguage: lang,
          }),
          ...(Array.isArray(data.faqs) && data.faqs.length
            ? [
                faqSchema(
                  data.faqs.map((f) => ({
                    question: pickLang(f.q, lang),
                    answer: pickLang(f.a, lang),
                  })),
                ),
              ]
            : []),
        ]}
      />

      {/* ===== TOP JUMP NAV — hop between the 3 services ===== */}
      <ServiceJumpNav activeSlug={slug} lang={lang} variant="top" />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" style={{ backgroundColor: soft }}>
        <BrandGraphic
          variant="wave"
          color={accent}
          className="absolute inset-0 w-full h-full opacity-[0.10] pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal as="div" variant="up" className="lg:col-span-7">
            <button
              type="button"
              onClick={() => navigate(`/${lang}/services`)}
              data-testid={`service-detail-${slug}-back`}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] font-mono-display uppercase font-bold mb-5 py-2 -my-2 hover:opacity-70 transition-opacity"
              style={{ color: accent }}
            >
              <ArrowRight className="h-3 w-3 rotate-180" />
              {isJa ? "サービス一覧" : "All services"}
            </button>

            {WORDMARK_MAP[slug] && (
              <div className="mb-6 flex items-center gap-4 md:gap-5 flex-wrap">
                {/* Brand-anchor icon to the LEFT of the wordmark.
                    Per owner directive: this is "how we represent who we are". */}
                {(slug === "japanetccard" || slug === "japantollreceipts") && (
                  <img loading="eager" decoding="async" fetchPriority="high"
                    src={IMG.etcGateBrand}
                    alt=""
                    aria-hidden="true"
                    className="block h-[80px] md:h-[100px] lg:h-[110px] w-auto select-none shrink-0"
                    draggable={false}
                  />
                )}
                {slug === "japanchangemoney" && (
                  <img loading="eager" decoding="async" fetchPriority="high"
                    src={IMG.japanchangemoneyIcon}
                    alt=""
                    aria-hidden="true"
                    className="block h-[80px] md:h-[100px] lg:h-[110px] w-auto select-none shrink-0"
                    draggable={false}
                  />
                )}
                <img loading="eager" decoding="async" fetchPriority="high"
                  src={WORDMARK_MAP[slug]}
                  alt={`${data.title[lang]} wordmark`}
                  className="block max-w-[420px] w-full h-auto select-none"
                  draggable={false}
                />
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap mb-4">
              <span
                className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"
                style={
                  isPlanned
                    ? { color: accent, border: `1px solid ${accent}` }
                    : { color: "white", backgroundColor: "#2F8F3C" }
                }
              >
                {!isPlanned && (
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white animate-brand-blink" />
                )}
                {isPlanned ? (isJa ? "準備中" : "Planned") : (isJa ? "運営中" : "Live")}
                {isPlanned && (
                  <AppTooltip
                    content={t(lang, "tooltips.changeMoneyPlanned")}
                    placement="right"
                    testId={`service-detail-${slug}-planned-tip`}
                  >
                    <button
                      type="button"
                      aria-label={isJa ? "準備中サービスの詳細" : "About this planned service"}
                      className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full text-current opacity-70 hover:opacity-100"
                    >
                      <Info className="h-3 w-3" strokeWidth={2.25} />
                    </button>
                  </AppTooltip>
                )}
              </span>
              <span className="text-[10px] tracking-[0.22em] font-mono-display uppercase text-[#64748B] font-bold">
                {data.domain}
              </span>
              {data.established && (
                <span className="text-[10px] tracking-[0.22em] font-mono-display uppercase text-[#64748B]">
                  · {isJa ? `創業 ${data.established}` : `Est. ${data.established}`}
                </span>
              )}
            </div>

            <h1
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-4xl md:text-5xl lg:text-[56px] font-bold tracking-tighter leading-[1.05]",
              ].join(" ")}
            >
              {data.headline[lang]}
            </h1>
            <p className="mt-5 text-[16px] md:text-[17px] leading-[1.7] text-[#475569] max-w-2xl">
              {data.intro[lang]}
            </p>

            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <a
                href={withLang(data.href, lang)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-detail-${slug}-visit`}
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full text-white text-[13px] font-semibold tracking-tight transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: accent }}
              >
                {isJa ? "公式サイトを見る" : "Visit"} · {data.domain}
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
              </a>
              {slug === "japanetccard" && (
                <a
                  href="https://www.japancardetc.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-detail-japanetccard-dev-site"
                  className="group inline-flex items-center gap-2 px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight border-2 border-dashed transition-all hover:-translate-y-0.5 bg-white"
                  style={{ borderColor: accent, color: accent }}
                  title={isJa
                    ? "次世代サイト（2026年12月31日 公開予定）"
                    : "Next-gen production site — launching December 31, 2026"}
                >
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="flex flex-col leading-tight text-left">
                    <span className="text-[9.5px] tracking-[0.18em] font-mono-display uppercase font-bold opacity-70">
                      {isJa ? "新サイト · 2026/12/31 公開" : "New site · Launches 12/31/2026"}
                    </span>
                    <span>www.JapanCardETC.com</span>
                  </span>
                  <ArrowUpRight
                    className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.5}
                  />
                </a>
              )}
              {slug === "japantollreceipts" && (
                <span
                  data-testid="service-detail-japantollreceipts-launch-pill"
                  className="inline-flex items-center gap-2 px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight border-2 border-dashed bg-white"
                  style={{ borderColor: accent, color: accent }}
                  title={isJa
                    ? "新本番サイト 2026年12月31日 公開予定"
                    : "New production site launching December 31, 2026"}
                >
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="flex flex-col leading-tight text-left">
                    <span className="text-[9.5px] tracking-[0.18em] font-mono-display uppercase font-bold opacity-70">
                      {isJa ? "新本番サイト" : "New production site"}
                    </span>
                    <span>{isJa ? "2026年12月31日 公開予定" : "Launches 12/31/2026"}</span>
                  </span>
                </span>
              )}
              {data.support?.email && (
                <a
                  href={`mailto:${data.support.email}`}
                  data-testid={`service-detail-${slug}-mail`}
                  className="inline-flex items-center gap-2 px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight border-2 transition-colors hover:bg-white"
                  style={{ borderColor: accent, color: accent }}
                >
                  <Mail className="h-3.5 w-3.5" strokeWidth={2.25} />
                  {data.support.email}
                </a>
              )}
            </div>
          </Reveal>

          {/* HERO ARTWORK */}
          <Reveal as="div" variant="up" delay={150} className="lg:col-span-5">
            {slug === "japanetccard" && SAMPLE_CARD.japanetccard ? (
              <div
                className="relative w-full rounded-2xl overflow-hidden bg-white shadow-[0_30px_60px_-20px_rgba(15,29,45,0.35)] ring-1 ring-[#E2E8F0] rotate-[-2deg] hover:rotate-0 transition-transform duration-500"
                data-testid={`service-detail-${slug}-card-art`}
              >
                <img loading="eager" decoding="async" fetchPriority="high"
                  src={SAMPLE_CARD.japanetccard}
                  alt={isJa ? "JapanETCcardのサンプルカード" : "JapanETCcard sample card"}
                  className="block w-full h-auto"
                />
              </div>
            ) : slug === "japantollreceipts" ? (
              <ReceiptsBrandPanel accent={accent} isJa={isJa} />
            ) : slug === "japanchangemoney" ? (
              <ExchangeBrandPanel accent={accent} isJa={isJa} />
            ) : EDITORIAL_IMG[slug] ? (
              <div
                className="relative aspect-[5/4] w-full overflow-hidden rounded-2xl shadow-[0_25px_55px_-25px_rgba(15,29,45,0.35)]"
                data-testid={`service-detail-${slug}-hero-image`}
              >
                <img
                  src={EDITORIAL_IMG[slug]}
                  alt={`${data.domain} — ${pickLang(data.title, lang)}`}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div
                  className="absolute inset-0 mix-blend-multiply opacity-30"
                  style={{
                    background: `linear-gradient(135deg, ${accent} 0%, transparent 60%)`,
                  }}
                />
              </div>
            ) : null}
          </Reveal>
        </div>
      </section>

      {/* ===== AT A GLANCE / STATS ===== */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-12">
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6">
            {data.stats.map((s, si) => (
              <li key={si} className="border-l-2 pl-4" style={{ borderColor: accent }}>
                <div className="font-display text-[#0F1D2D] text-[22px] md:text-[24px] font-bold tracking-tight leading-tight">
                  {s.value ?? (isJa ? s.valueJa : s.valueEn)}
                </div>
                <div className="mt-1.5 text-[10.5px] tracking-[0.18em] font-mono-display text-[#64748B] uppercase leading-snug">
                  {isJa ? s.labelJa : s.labelEn}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== MEET THE TEAM (service-specific) ===== */}
      {slug === "japanetccard" && (
        <ServiceTeamBand
          lang={lang}
          slug={slug}
          accent={accent}
          softBg={data.accentSoft || "#F8F9FA"}
          team={JEC_TEAM}
          groupPhotos={JEC_TEAM_GROUP_PHOTOS}
          manifesto={JEC_TEAM_MANIFESTO}
        />
      )}
      {slug === "japantollreceipts" && (
        <ServiceTeamBand
          lang={lang}
          slug={slug}
          accent={accent}
          softBg={data.accentSoft || "#F8F9FA"}
          team={JTR_TEAM}
          groupPhotos={JTR_TEAM_GROUP_PHOTOS}
          manifesto={JTR_TEAM_MANIFESTO}
        />
      )}

      {/* ===== TESTIMONIAL SPOTLIGHT (service-specific) ===== */}
      {slug === "japanetccard" && (
        <TestimonialRotator
          lang={lang}
          mode="spotlight"
          accent={accent}
          softBg={data.accentSoft || "#F4EEF9"}
          eyebrowEn="Featured customer story"
          eyebrowJa="注目のお客様の声"
          headlineEn="Why customers stay with us."
          headlineJa="お客様が選び続けてくださる理由。"
          serviceId="japanetccard"
          linkTo={`/${lang}/testimonials`}
          testIdSurface={`detail-${slug}-spotlight`}
        />
      )}
      {slug === "japantollreceipts" && (
        <TestimonialRotator
          lang={lang}
          mode="spotlight"
          accent={accent}
          softBg={data.accentSoft || "#EFF8FF"}
          eyebrowEn="Featured customer story"
          eyebrowJa="注目のお客様の声"
          headlineEn="Why customers trust their reports to us."
          headlineJa="お客様がレポートを私たちに任せてくださる理由。"
          serviceId="japantollreceipts"
          linkTo={`/${lang}/testimonials`}
          testIdSurface={`detail-${slug}-spotlight`}
        />
      )}

      {/* ===== OPERATING DISCIPLINE — service-tuned SVG/CSS visualizations =====
            Service-tuned mini-board of pure SVG/CSS animations that visually
            tell the operating-discipline story (pipelines, accuracy gauges,
            human-in-the-loop, multilingual, etc.). Per service slug we pick
            6 visualizations from the library that best match the product.
            See `components/ServiceVisuals.jsx` for the full catalog. */}
      <ServiceVisualsBoard slug={slug} accent={accent} isJa={isJa} />

      {/* ===== WHO IT'S FOR (Audiences) ===== */}
      {data.audiences && data.audiences.length > 0 && (
        <section className="bg-[#F8F9FA] border-b border-[#E2E8F0]" data-testid={`service-detail-${slug}-audiences`}>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="max-w-3xl">
              <SectionEyebrow accent={accent}>
                <Users className="h-3 w-3 inline mr-1.5" strokeWidth={2.5} />
                {isJa ? "支えるお客様" : "Who it's for"}
              </SectionEyebrow>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-3xl md:text-[40px] font-bold tracking-tighter leading-[1.1]",
                ].join(" ")}
              >
                {isJa
                  ? "日本で運転・通行・記録を必要とする方々のために。"
                  : "Built around the people who drive, commute and keep records in Japan."}
              </h2>
            </div>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.audiences.map((a, i) => (
                <Reveal as="li" variant="up" delay={i * 60} key={i}>
                  <div
                    className="h-full rounded-xl bg-white border border-[#E2E8F0] px-4 py-4 flex items-start gap-3 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.22)]"
                    data-testid={`service-detail-${slug}-audience-${i}`}
                  >
                    <span
                      aria-hidden
                      className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: accent }}
                    />
                    <span className="text-[14px] font-medium text-[#0F1D2D] leading-snug">
                      {a[lang]}
                    </span>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          OUTDOOR PRESENCE — JapanETCcard signboards across Japan.
          Owner directive: every signboard on file currently advertises
          JapanETCcard, so on this service page we surface the full
          set as clickable photo cards that deep-link straight to each
          board's detail page. Other services don't render this band.
          ════════════════════════════════════════════════════════════════ */}
      {slug === "japanetccard" && (
        <SignboardsShowcase
          lang={lang}
          variant="service"
          testIdPrefix="service-detail-japanetccard-signboards"
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          WHY WE BUILT THIS — founder/origin narrative per service.
          Lives in SERVICE_NARRATIVE[slug].whyBuilt. Additive; does not
          replace any earlier intro copy. Added 2026-02-19.
          ════════════════════════════════════════════════════════════════ */}
      {narrative?.whyBuilt && (
        <section
          className="relative isolate bg-white border-b border-[#E2E8F0]"
          data-testid={`service-detail-${slug}-why-built`}
        >
          <PageSideAura testId="service-detail-why-built-aura" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <SectionEyebrow accent={accent}>
              <Sparkles className="h-3 w-3 inline mr-1.5" strokeWidth={2.5} />
              {isJa ? narrative.whyBuilt.eyebrowJa : narrative.whyBuilt.eyebrowEn}
            </SectionEyebrow>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "mt-3 text-[#0F1D2D] text-3xl md:text-[40px] font-bold tracking-tighter leading-[1.1]",
              ].join(" ")}
            >
              {isJa ? narrative.whyBuilt.titleJa : narrative.whyBuilt.titleEn}
            </h2>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <p className="lg:col-span-8 text-[15px] md:text-[16px] leading-[1.9] text-[#475569]">
                {isJa ? narrative.whyBuilt.bodyJa : narrative.whyBuilt.bodyEn}
              </p>
              <ul
                className="lg:col-span-4 rounded-2xl p-5 md:p-6 ring-1 ring-[#E2E8F0] bg-white space-y-3"
                style={{ borderLeft: `4px solid ${accent}` }}
              >
                {(isJa ? narrative.whyBuilt.pillarsJa : narrative.whyBuilt.pillarsEn).map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span
                      aria-hidden
                      className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: accent }}
                    />
                    <span className="text-[13.5px] md:text-[14px] text-[#0F1D2D] leading-snug font-medium">
                      {p}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Founder's message CTA — only for services that have a long-form
                founder narrative authored in `data/foundersMessages.js`.
                Hidden when no such page exists (e.g. JapanChangeMoney). */}
            {["japanetccard", "japantollreceipts"].includes(slug) && (
              <div
                className="mt-10 md:mt-12 rounded-2xl ring-1 ring-[#E2E8F0] bg-white overflow-hidden"
                data-testid={`service-detail-${slug}-founders-message-cta`}
              >
                <Link
                  to={`/${lang}/founders-message/${slug}`}
                  className="group flex items-center gap-5 md:gap-7 p-5 md:p-6 hover:bg-[#FAFBFD] transition-colors"
                  data-testid={`service-detail-${slug}-founders-message-link`}
                >
                  <img
                    src="/founder/adam-jones.png"
                    alt="Adam Jones — Founder & CEO"
                    width="72"
                    height="72"
                    loading="lazy"
                    className="h-16 w-16 md:h-[72px] md:w-[72px] rounded-full object-cover ring-2 ring-white shadow-md shrink-0"
                    style={{ outline: `2px solid ${accent}` }}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[10px] md:text-[10.5px] tracking-[0.22em] uppercase font-bold font-mono-display mb-1.5"
                      style={{ color: accent }}
                    >
                      {isJa ? "創業者からのメッセージ" : "From the Founder"}
                    </div>
                    <h3
                      className={[
                        isJa ? "font-ja" : "font-display",
                        "text-[#0F1D2D] text-lg md:text-[22px] font-bold leading-tight tracking-tight",
                      ].join(" ")}
                    >
                      {isJa
                        ? slug === "japantollreceipts"
                          ? "なぜJapanTollReceiptsを立ち上げたのか ─ 全文を読む"
                          : "なぜJapanETCcardを立ち上げたのか ─ 全文を読む"
                        : slug === "japantollreceipts"
                          ? "Why we built JapanTollReceipts — read the full story"
                          : "Why we built JapanETCcard — read the full story"}
                    </h3>
                    <p className="mt-1.5 text-[13px] md:text-[14px] text-[#475569] leading-snug">
                      {isJa
                        ? "アダム・ジョーンズ（創業者・代表取締役CEO）が語る、サービス誕生の経緯とこれから。"
                        : "Adam Jones, Founder & CEO, on the journey that led to this service — and what comes next."}
                    </p>
                  </div>
                  <ArrowRight
                    className="hidden sm:block h-5 w-5 text-[#94A3B8] group-hover:translate-x-1 group-hover:text-[#0F1D2D] transition-all shrink-0"
                    aria-hidden
                  />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          NEXT-GENERATION SITE MILESTONE — JapanETCcard only.
          Announces the upcoming JapanCardETC.com platform (in integration
          testing, planned 2026 launch). Owner directive 2026-05-19:
          highlight this as a key milestone for JEC's automation roadmap.
          ════════════════════════════════════════════════════════════════ */}
      {slug === "japanetccard" && (
        <section
          className="relative isolate overflow-hidden border-b border-[#E2E8F0]"
          data-testid="service-detail-japanetccard-next-gen"
          style={{
            background:
              "linear-gradient(135deg, #0E2B5C 0%, #5B2E83 55%, #1A3A6E 100%)",
          }}
        >
          {/* Decorative blurred accents — branded blue/purple/green glow */}
          <span
            aria-hidden
            className="pointer-events-none absolute -top-24 -left-20 h-[420px] w-[420px] rounded-full blur-[140px] opacity-50"
            style={{ background: "radial-gradient(circle, #4F8FE0 0%, transparent 70%)" }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -right-20 h-[460px] w-[460px] rounded-full blur-[150px] opacity-40"
            style={{ background: "radial-gradient(circle, #7BD389 0%, transparent 70%)" }}
          />

          <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <Reveal variant="up">
              <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-12 items-center">
                {/* LEFT — narrative */}
                <div>
                  <div
                    className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-5 border border-white/25 bg-white/10 backdrop-blur-sm"
                    data-testid="next-gen-eyebrow"
                  >
                    <Sparkles className="h-3 w-3 text-[#7BD389]" strokeWidth={2.5} />
                    <span className="text-[10.5px] tracking-[0.24em] font-mono-display uppercase font-bold text-white">
                      {isJa ? "2026年12月31日 公開予定 ─ 主要マイルストーン" : "Launching December 31, 2026 — Key milestone"}
                    </span>
                  </div>

                  <h2
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "text-white text-3xl md:text-[42px] font-bold tracking-tighter leading-[1.08] mb-5",
                    ].join(" ")}
                    data-testid="next-gen-heading"
                  >
                    {isJa
                      ? "次世代のJapanETCcard ─ JapanCardETC.com"
                      : "The next-generation JapanETCcard — JapanCardETC.com"}
                  </h2>

                  <p
                    className="text-[14.5px] md:text-[15.5px] leading-[1.8] text-white/85 mb-4 max-w-xl"
                    data-testid="next-gen-lede"
                  >
                    {isJa
                      ? "現在、www.JapanCardETC.com は新サイトの開発・検証用として稼働している一時的なドメインです。JapanETCcardのこれまでの実績を引き継ぎながら、より自動化された手続き・運用基盤・サポート体験を提供する次世代プラットフォームを、ここで段階的に構築しています。"
                      : "www.JapanCardETC.com is currently our temporary domain — a live staging environment where the next-generation site is being built and verified. It carries forward everything JapanETCcard has built since 2013, layered onto a more automated application, operations and support foundation."}
                  </p>

                  <p
                    className="text-[13.5px] md:text-[14px] leading-[1.8] text-white/75 mb-7 max-w-xl"
                    data-testid="next-gen-body"
                  >
                    {isJa
                      ? "統合テストと運用検証が完了するまでは、現行の JapanETCcard.com が引き続き正式なサービスサイトです。新プラットフォームの本番公開は 2026年12月31日 を予定しており、姉妹サービスの JapanTollReceipts.jp の新本番サイトと同日に切り替わります。"
                      : "Until integration testing and operational validation are complete, JapanETCcard.com remains the service of record. The new platform is scheduled to go live on December 31, 2026 — the same day our sister service JapanTollReceipts.jp launches its new production site, so the two next-generation experiences arrive together."}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <a
                      href="https://www.japancardetc.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="next-gen-cta-visit"
                      className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-white text-[#0E2B5C] text-[13.5px] font-bold tracking-tight transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-15px_rgba(255,255,255,0.55)]"
                    >
                      <span>{isJa ? "開発中のサイトを見る" : "Preview the dev site"}</span>
                      <ArrowUpRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={2.5}
                      />
                    </a>
                    <a
                      href={`/${lang}/roadmap?status=in_progress`}
                      data-testid="next-gen-cta-roadmap"
                      className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 border border-white/35 bg-white/5 text-white text-[13.5px] font-semibold tracking-tight backdrop-blur-sm transition-colors hover:bg-white/15"
                    >
                      {isJa ? "JapanETCcardの進行中ロードマップを見る" : "View JEC items in progress"}
                    </a>
                  </div>
                </div>

                {/* RIGHT — milestone facts card */}
                <div
                  className="rounded-3xl bg-white/8 backdrop-blur-md border border-white/15 p-7 md:p-8"
                  data-testid="next-gen-facts-card"
                >
                  <div className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#7BD389] mb-4">
                    {isJa ? "マイルストーン一覧" : "Milestone snapshot"}
                  </div>
                  <ul className="space-y-4">
                    {[
                      {
                        en: { k: "Dev site (temporary)", v: "www.JapanCardETC.com" },
                        ja: { k: "開発用ドメイン（暫定）", v: "www.JapanCardETC.com" },
                      },
                      {
                        en: { k: "Status", v: "Live staging — in integration testing" },
                        ja: { k: "現在の状態", v: "ステージング稼働中（統合テスト段階）" },
                      },
                      {
                        en: { k: "Target rollout", v: "December 31, 2026 — alongside the new JapanTollReceipts.jp production site" },
                        ja: { k: "本格切替予定", v: "2026年12月31日 ─ JapanTollReceipts.jpの新本番サイトと同時公開" },
                      },
                      {
                        en: { k: "Continuity", v: "JapanETCcard.com remains the service of record" },
                        ja: { k: "サービス継続", v: "切り替えまではJapanETCcard.comが正式サイト" },
                      },
                      {
                        en: { k: "What changes", v: "More automation across application, operations and support" },
                        ja: { k: "変わる点", v: "手続き・運用・サポートの自動化をさらに強化" },
                      },
                    ].map((row, i) => {
                      const r = isJa ? row.ja : row.en;
                      return (
                        <li key={i} className="flex flex-col gap-1">
                          <span className="text-[10.5px] tracking-[0.2em] font-mono-display uppercase font-semibold text-white/55">
                            {r.k}
                          </span>
                          <span className="text-[13.5px] leading-[1.55] text-white font-medium">
                            {r.v}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          NEXT-GENERATION SITE MILESTONE — JapanTollReceipts only.
          Announces the new JTR production site launching alongside the
          new JapanCardETC.com on December 31, 2026. Brand-green theme.
          ════════════════════════════════════════════════════════════════ */}
      {slug === "japantollreceipts" && (
        <section
          className="relative isolate overflow-hidden border-b border-[#E2E8F0]"
          data-testid="service-detail-japantollreceipts-next-gen"
          style={{
            background:
              "linear-gradient(135deg, #134B26 0%, #2F8F3C 55%, #0E5FB0 100%)",
          }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -top-24 -left-20 h-[420px] w-[420px] rounded-full blur-[140px] opacity-50"
            style={{ background: "radial-gradient(circle, #7BD389 0%, transparent 70%)" }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -right-20 h-[460px] w-[460px] rounded-full blur-[150px] opacity-40"
            style={{ background: "radial-gradient(circle, #4F8FE0 0%, transparent 70%)" }}
          />

          <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <Reveal variant="up">
              <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-12 items-center">
                <div>
                  <div
                    className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-5 border border-white/25 bg-white/10 backdrop-blur-sm"
                    data-testid="jtr-next-gen-eyebrow"
                  >
                    <Sparkles className="h-3 w-3 text-[#7BD389]" strokeWidth={2.5} />
                    <span className="text-[10.5px] tracking-[0.24em] font-mono-display uppercase font-bold text-white">
                      {isJa ? "2026年12月31日 公開予定 ─ 主要マイルストーン" : "Launching December 31, 2026 — Key milestone"}
                    </span>
                  </div>

                  <h2
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "text-white text-3xl md:text-[42px] font-bold tracking-tighter leading-[1.08] mb-5",
                    ].join(" ")}
                    data-testid="jtr-next-gen-heading"
                  >
                    {isJa
                      ? "次世代のJapanTollReceipts.jp ─ 新本番サイト公開"
                      : "The next-generation JapanTollReceipts.jp — new production site"}
                  </h2>

                  <p
                    className="text-[14.5px] md:text-[15.5px] leading-[1.8] text-white/85 mb-4 max-w-xl"
                    data-testid="jtr-next-gen-lede"
                  >
                    {isJa
                      ? "JapanTollReceipts.jp の新しい本番サイトは、2026年12月31日 に公開予定です。日次・週次・月次の ETC レポート配信を中核に、検証パイプライン・PDF / CSV 出力・お客様ダッシュボードを刷新し、より速く・より一貫した運用体験をお届けします。"
                      : "A brand-new JapanTollReceipts.jp production site is scheduled to go live on December 31, 2026. Built around the same daily, weekly and monthly ETC report cadence customers rely on today, the new platform refreshes the validation pipeline, PDF / CSV output, and customer dashboard for a faster, more consistent operational experience."}
                  </p>

                  <p
                    className="text-[13.5px] md:text-[14px] leading-[1.8] text-white/75 mb-7 max-w-xl"
                    data-testid="jtr-next-gen-body"
                  >
                    {isJa
                      ? "新サイトは姉妹サービス JapanCardETC.com の本番公開と同日に切り替わります。お客様のレポート配信・契約・サポート窓口に支障が出ないよう、運用検証が完了したうえで段階的に移行します。"
                      : "The launch is coordinated with the new JapanCardETC.com production site — both go live on the same day. We will roll over to the new platform once end-to-end operational validation is complete, with no interruption to your scheduled reports, account or support."}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <a
                      href={withLang(data.href, lang)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="jtr-next-gen-cta-visit"
                      className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-white text-[#134B26] text-[13.5px] font-bold tracking-tight transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-15px_rgba(255,255,255,0.55)]"
                    >
                      <span>{isJa ? "現行のJapanTollReceipts.jpを見る" : "Visit JapanTollReceipts.jp today"}</span>
                      <ArrowUpRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={2.5}
                      />
                    </a>
                    <a
                      href={`/${lang}/roadmap?status=in_progress`}
                      data-testid="jtr-next-gen-cta-roadmap"
                      className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 border border-white/35 bg-white/5 text-white text-[13.5px] font-semibold tracking-tight backdrop-blur-sm transition-colors hover:bg-white/15"
                    >
                      {isJa ? "JTRの進行中ロードマップを見る" : "View JTR items in progress"}
                    </a>
                  </div>
                </div>

                <div
                  className="rounded-3xl bg-white/8 backdrop-blur-md border border-white/15 p-7 md:p-8"
                  data-testid="jtr-next-gen-facts-card"
                >
                  <div className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#7BD389] mb-4">
                    {isJa ? "マイルストーン一覧" : "Milestone snapshot"}
                  </div>
                  <ul className="space-y-4">
                    {[
                      {
                        en: { k: "Launch date", v: "December 31, 2026" },
                        ja: { k: "公開予定日", v: "2026年12月31日" },
                      },
                      {
                        en: { k: "What launches", v: "New JapanTollReceipts.jp production site" },
                        ja: { k: "公開対象", v: "JapanTollReceipts.jpの新本番サイト" },
                      },
                      {
                        en: { k: "Paired launch", v: "Same day as the new JapanCardETC.com production site" },
                        ja: { k: "同時公開", v: "JapanCardETC.comの新本番サイトと同日公開" },
                      },
                      {
                        en: { k: "Continuity", v: "JapanTollReceipts.jp remains the service of record until launch" },
                        ja: { k: "サービス継続", v: "切り替えまではJapanTollReceipts.jpが正式サイト" },
                      },
                      {
                        en: { k: "What changes", v: "Faster validation pipeline, refreshed dashboards, modernized PDF / CSV output" },
                        ja: { k: "変わる点", v: "検証パイプラインの高速化、ダッシュボード刷新、PDF / CSV 出力の刷新" },
                      },
                    ].map((row, i) => {
                      const r = isJa ? row.ja : row.en;
                      return (
                        <li key={i} className="flex flex-col gap-1">
                          <span className="text-[10.5px] tracking-[0.2em] font-mono-display uppercase font-semibold text-white/55">
                            {r.k}
                          </span>
                          <span className="text-[13.5px] leading-[1.55] text-white font-medium">
                            {r.v}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ===== CAPABILITIES (full feature list) ===== */}
      <section className="relative isolate bg-white border-b border-[#E2E8F0]" data-testid={`service-detail-${slug}-features`}>
        <PageSideAura testId="service-detail-features-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-3xl mb-10">
            <SectionEyebrow accent={accent}>
              <Sparkles className="h-3 w-3 inline mr-1.5" strokeWidth={2.5} />
              {isJa ? "提供している/予定の機能" : "What it does"}
            </SectionEyebrow>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-[40px] font-bold tracking-tighter leading-[1.1]",
              ].join(" ")}
            >
              {isPlanned
                ? isJa
                  ? "公開時に提供を予定している機能。"
                  : "Capabilities planned for launch."
                : isJa
                ? "毎日の運用で実際に役立つ機能。"
                : "Capabilities that quietly do the work every day."}
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {data.features.map((f, fi) => (
              <Reveal as="li" variant="up" delay={fi * 60} key={fi}>
                <div className="flex gap-4">
                  <span
                    aria-hidden
                    className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: accent }}
                  />
                  <div>
                    <h3 className="text-[15px] md:text-[16px] font-bold text-[#0F1D2D] tracking-tight leading-snug">
                      {isJa ? f.titleJa : f.titleEn}
                    </h3>
                    <p className="mt-1.5 text-[13.5px] leading-[1.75] text-[#475569]">
                      {isJa ? f.bodyJa : f.bodyEn}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== FEATURES IN DEPTH — every card links out to the corresponding
            feature page on the operating service site. Source of truth lives
            there, not here, so visuals and copy can't drift out of sync. */}
      {data.featureLinks && data.featureLinks.length > 0 && (
        <section
          className="bg-[#F8F9FA] border-b border-[#E2E8F0]"
          data-testid={`service-detail-${slug}-feature-links`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div className="max-w-3xl">
                <SectionEyebrow accent={accent}>
                  {isJa ? "詳しく見る" : "Explore in detail"}
                </SectionEyebrow>
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-3xl md:text-[40px] font-bold tracking-tighter leading-[1.1]",
                  ].join(" ")}
                >
                  {isJa
                    ? `${data.domain} で深く知る。`
                    : `Go deeper on ${data.domain}.`}
                </h2>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-[#475569] max-w-2xl">
                  {isJa
                    ? "各機能の詳細は、運営サービスのサイトで最新の情報を確認できます。"
                    : "Each feature has a dedicated page on the operating service site — always kept up to date there."}
                </p>
              </div>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {data.featureLinks.map((f, fi) => (
                <Reveal as="li" variant="up" delay={fi * 60} key={fi}>
                  <a
                    href={withLang(f.href, lang)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-detail-${slug}-feature-link-${fi}`}
                    className="group h-full block rounded-2xl bg-white border border-[#E2E8F0] p-5 md:p-6 transition-all hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_25px_55px_-30px_rgba(15,29,45,0.35)] relative overflow-hidden"
                  >
                    <span
                      aria-hidden
                      className="absolute top-0 left-0 right-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: accent }}
                    />
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="text-[16px] md:text-[17px] font-bold text-[#0F1D2D] tracking-tight leading-snug">
                        {isJa ? f.titleJa : f.titleEn}
                      </h3>
                      <ArrowUpRight
                        className="h-4 w-4 shrink-0 text-[#94A3B8] group-hover:text-[#0F1D2D] transition-colors mt-0.5"
                        strokeWidth={2.25}
                      />
                    </div>
                    {f.tags && f.tags.length > 0 && (
                      <ul className="flex flex-wrap gap-1.5 mb-3" data-testid={`service-detail-${slug}-feature-link-${fi}-tags`}>
                        {f.tags.map((t) => (
                          <li
                            key={t}
                            className="text-[9.5px] tracking-[0.16em] font-mono-display uppercase font-bold px-1.5 py-0.5 rounded"
                            style={{ color: accent, backgroundColor: `${accent}10` }}
                          >
                            {t}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-[13px] leading-[1.7] text-[#475569]">
                      {isJa ? f.summaryJa : f.summaryEn}
                    </p>
                    <div
                      className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold opacity-80 group-hover:opacity-100 transition-opacity"
                      style={{ color: accent }}
                    >
                      {data.domain}
                      <ExternalLink className="h-3 w-3" strokeWidth={2.25} />
                    </div>
                  </a>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ===== PLANS (visit site) — summarize each plan but don't duplicate
            pricing. The truth lives on the operating service site so we
            never get out of sync when prices change. ===== */}
      {data.planLinks && data.planLinks.length > 0 && (
        <section
          className="relative isolate bg-white border-b border-[#E2E8F0]"
          data-testid={`service-detail-${slug}-plan-links`}
        >
          <PageSideAura testId="service-detail-plans-aura" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="max-w-3xl mb-10">
              <SectionEyebrow accent={accent}>
                {isJa ? "プラン" : "Plans"}
              </SectionEyebrow>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-3xl md:text-[40px] font-bold tracking-tighter leading-[1.1]",
                ].join(" ")}
              >
                {isJa
                  ? "ご利用シーンに合わせたプラン。"
                  : "Plans for every kind of customer."}
              </h2>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-[#475569] max-w-2xl">
                {isJa
                  ? "最新の料金・条件・特典は、各運営サービスのサイトで最新情報をご確認ください。"
                  : "Current pricing, terms and offers are always maintained on the operating service site — single source of truth."}
              </p>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {data.planLinks.map((p, pi) => (
                <Reveal as="li" variant="up" delay={pi * 70} key={pi}>
                  <a
                    href={withLang(p.href, lang)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-detail-${slug}-plan-link-${pi}`}
                    className="group h-full flex flex-col rounded-2xl bg-white border border-[#E2E8F0] p-6 transition-all hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_25px_55px_-30px_rgba(15,29,45,0.35)] relative overflow-hidden"
                  >
                    <span
                      aria-hidden
                      className="absolute top-0 left-0 right-0 h-[3px]"
                      style={{ backgroundColor: accent }}
                    />
                    <div
                      className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold mb-3"
                      style={{ color: accent }}
                    >
                      {isJa ? p.nameJa : p.nameEn}
                    </div>
                    <p className="text-[13.5px] leading-[1.75] text-[#475569] flex-1">
                      {isJa ? p.summaryJa : p.summaryEn}
                    </p>
                    <div className="mt-5 inline-flex items-center justify-between gap-2 text-[12px] tracking-tight font-semibold pt-4 border-t border-[#E2E8F0]">
                      <span style={{ color: accent }} className="inline-flex items-center gap-1.5">
                        {isJa ? "プランを見る" : "View on site"}
                        <ArrowUpRight
                          className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          strokeWidth={2.25}
                        />
                      </span>
                      <span className="text-[10px] tracking-[0.22em] font-mono-display uppercase text-[#94A3B8]">
                        {data.domain}
                      </span>
                    </div>
                  </a>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ===== LATEST CHANGELOG SNAPSHOT ===== */}
      {serviceReleases.length > 0 && (
        <section className="relative isolate bg-white border-b border-[#E2E8F0]" data-testid={`service-detail-${slug}-changelog`}>
          <PageSideAura testId="service-detail-changelog-aura" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div className="max-w-3xl">
                <SectionEyebrow accent={accent}>
                  <Package className="h-3 w-3 inline mr-1.5" strokeWidth={2.5} />
                  {isJa ? "最近の更新" : "Recent updates"}
                </SectionEyebrow>
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa
                    ? `${data.domain} が最近、開発したもの。`
                    : `What ${data.domain} has developed recently.`}
                </h2>
                {serviceFreshness.length > 0 && (
                  <SourceFreshnessBadge
                    sourceFreshness={serviceFreshness}
                    lang={lang}
                    className="mt-3"
                  />
                )}
              </div>
              <Link
                to={`/${lang}/whats-new`}
                data-testid={`service-detail-${slug}-changelog-viewall`}
                className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.22em] font-mono-display uppercase font-bold hover:opacity-70 transition-opacity"
                style={{ color: accent }}
              >
                {isJa ? "すべて見る" : "View all updates"}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Link>
            </div>

            <ul className="space-y-3">
              {serviceReleases.map((r, ri) => (
                <Reveal as="li" variant="up" delay={ri * 60} key={r.version}>
                  <Link
                    to={`/${lang}/whats-new?release=${encodeURIComponent(r.version)}`}
                    data-testid={`service-detail-${slug}-changelog-item-${r.version}`}
                    className="group flex items-start gap-4 p-4 md:p-5 rounded-xl border border-[#E2E8F0] bg-white hover:border-transparent hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.22)] transition-all"
                  >
                    <div className="flex flex-col gap-1.5 shrink-0 min-w-[120px]">
                      <div className="inline-flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5" style={{ color: accent }} />
                        <span className="text-[14px] font-bold" style={{ color: accent }}>
                          {r.version}
                        </span>
                      </div>
                      {r.latest && (
                        <span
                          className="self-start text-[9px] tracking-[0.22em] font-mono-display uppercase font-bold px-1.5 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: accent }}
                        >
                          {isJa ? "最新" : "Latest"}
                        </span>
                      )}
                      <span className="text-[11px] text-[#94A3B8] inline-flex items-center gap-1">
                        <Calendar className="h-2.5 w-2.5" />
                        {formatPublicDate(r.sortDate || r.date, lang)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[14.5px] md:text-[15px] font-semibold text-[#0F1D2D] leading-snug">
                        {pickLang(r.title, lang)}
                      </h3>
                      {pickLang(r.desc, lang) && (
                        <p className="mt-1.5 text-[12.5px] text-[#475569] leading-relaxed line-clamp-2">
                          {pickLang(r.desc, lang)}
                        </p>
                      )}
                    </div>
                    <ArrowUpRight
                      className="h-4 w-4 text-[#94A3B8] group-hover:text-[#0F1D2D] transition-colors shrink-0 mt-1"
                      strokeWidth={2.25}
                    />
                  </Link>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ===== ROADMAP SNAPSHOT ===== */}
      {serviceMilestones.length > 0 && (
        <section className="bg-[#F8F9FA] border-b border-[#E2E8F0]" data-testid={`service-detail-${slug}-roadmap`}>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div className="max-w-3xl">
                <SectionEyebrow accent={accent}>
                  <MapPinned className="h-3 w-3 inline mr-1.5" strokeWidth={2.5} />
                  {isJa ? "今後の取り組み" : "What's planned next"}
                </SectionEyebrow>
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa
                    ? `${data.domain} の公開ロードマップ。`
                    : `Public roadmap items for ${data.domain}.`}
                </h2>
              </div>
              <Link
                to={`/${lang}/roadmap`}
                data-testid={`service-detail-${slug}-roadmap-viewall`}
                className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.22em] font-mono-display uppercase font-bold hover:opacity-70 transition-opacity"
                style={{ color: accent }}
              >
                {isJa ? "ロードマップ全体" : "Full roadmap"}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Link>
            </div>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {serviceMilestones.map((m, mi) => {
                const statusMeta = dataset.STATUS_META[m.status] || dataset.STATUS_META.planned;
                return (
                  <Reveal as="li" variant="up" delay={mi * 60} key={m.id}>
                    <div
                      data-testid={`service-detail-${slug}-roadmap-item-${mi}`}
                      className="h-full rounded-xl bg-white border border-[#E2E8F0] p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.22)]"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          aria-hidden
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: statusMeta.dot }}
                        />
                        <span
                          className="text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold"
                          style={{ color: statusMeta.dot }}
                        >
                          {isJa ? statusMeta.ja : statusMeta.en}
                        </span>
                        <span className="ml-auto text-[10px] tracking-[0.16em] font-mono-display uppercase text-[#94A3B8]">
                          {m.quarter && m.quarter !== "unscheduled"
                            ? m.quarter.replace(/q(\d)_(\d{4})/, isJa ? "$2年Q$1" : "Q$1 $2")
                            : isJa ? "未定" : "Unscheduled"}
                        </span>
                      </div>
                      <h3 className="text-[14.5px] font-semibold text-[#0F1D2D] leading-snug">
                        {pickLang(m.title, lang)}
                      </h3>
                      {pickLang(m.desc, lang) && (
                        <p className="mt-2 text-[12.5px] text-[#475569] leading-relaxed line-clamp-3">
                          {pickLang(m.desc, lang)}
                        </p>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          PART OF NOJO ENTERPRISE — corporate connector. Ties this service
          to its parent pillar (mobility / records / digital infrastructure)
          and links back to About + Services hub. Additive section; lives
          in SERVICE_NARRATIVE[slug].partOfNojo. Added 2026-02-19.
          ════════════════════════════════════════════════════════════════ */}
      {narrative?.partOfNojo && (
        <section
          className="bg-[#F8F9FA] border-b border-[#E2E8F0]"
          data-testid={`service-detail-${slug}-part-of-nojo`}
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* LEFT: copy */}
              <div className="lg:col-span-7">
                <SectionEyebrow accent={accent}>
                  {isJa ? "NoJo Enterprise の事業として" : "Part of NoJo Enterprise"}
                </SectionEyebrow>
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "mt-3 text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa
                    ? `${data.domain} が NoJo Enterprise で果たす役割`
                    : `How ${data.domain} fits into NoJo Enterprise`}
                </h2>
                <p className="mt-5 text-[14.5px] md:text-[15.5px] leading-[1.85] text-[#475569]">
                  {isJa ? narrative.partOfNojo.bodyJa : narrative.partOfNojo.bodyEn}
                </p>
                <div className="mt-6 flex flex-wrap gap-x-7 gap-y-3">
                  <Link
                    to={`/${lang}/about`}
                    data-testid={`service-detail-${slug}-cta-about`}
                    className="inline-flex items-center gap-2 text-[12.5px] tracking-[0.16em] font-mono-display uppercase font-bold border-b-2 pb-1 transition-colors"
                    style={{ color: accent, borderColor: accent }}
                  >
                    {isJa ? "NoJo Enterpriseについて" : "About NoJo Enterprise"}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </Link>
                  <Link
                    to={`/${lang}/services`}
                    data-testid={`service-detail-${slug}-cta-services`}
                    className="inline-flex items-center gap-2 text-[12.5px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#0F1D2D] hover:text-[#0E5FB0] transition-colors"
                  >
                    {isJa ? "他のサービスを見る" : "View all services"}
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </Link>
                </div>
              </div>

              {/* RIGHT: pillar card — tri-stripe top, this service's pillar highlighted */}
              <div className="lg:col-span-5">
                <div className="relative rounded-2xl bg-white ring-1 ring-[#E2E8F0] p-6 md:p-7 overflow-hidden">
                  <span
                    aria-hidden
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{
                      background:
                        "linear-gradient(90deg, #5B2E83 0%, #2F8F3C 50%, #0E5FB0 100%)",
                    }}
                  />
                  <div className="text-[10.5px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#475569] mb-4 mt-2">
                    {isJa ? "事業の柱" : "Operating pillars"}
                  </div>
                  <ul className="space-y-3">
                    {[
                      { id: "mobility",     color: "#5B2E83", ja: "モビリティ",       en: "Mobility" },
                      { id: "records",      color: "#2F8F3C", ja: "記録・レポート",   en: "Records" },
                      { id: "digitalInfra", color: "#0E5FB0", ja: "デジタル基盤",     en: "Digital Infrastructure" },
                    ].map((p) => {
                      const active = p.id === narrative.partOfNojo.pillar;
                      return (
                        <li
                          key={p.id}
                          className={[
                            "flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 transition-colors",
                            active ? "ring-1" : "",
                          ].join(" ")}
                          style={
                            active
                              ? { backgroundColor: `${p.color}10`, borderColor: p.color, "--tw-ring-color": p.color }
                              : undefined
                          }
                          data-testid={`service-detail-${slug}-pillar-${p.id}${active ? "-active" : ""}`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span
                              aria-hidden
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: p.color }}
                            />
                            <span
                              className={[
                                "text-[13.5px] leading-tight",
                                active ? "font-bold text-[#0F1D2D]" : "text-[#475569]",
                              ].join(" ")}
                            >
                              {isJa ? p.ja : p.en}
                            </span>
                          </div>
                          {active && (
                            <span
                              className="text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold shrink-0"
                              style={{ color: p.color }}
                            >
                              {isJa ? "本サービス" : "This service"}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="mt-5 text-[11.5px] tracking-[0.04em] text-[#64748B] leading-relaxed">
                    {isJa
                      ? "各サービスは独立して運営しつつ、現場で確認した改善点は、お客様データを共有することなく NoJo 全体の運営品質向上に活かしています。"
                      : "Each service is run independently, but operational lessons learned in one are quietly applied across all — without ever sharing customer data."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== FAQ — visible <details> disclosure list, paired with the
              FAQPage JSON-LD emitted up top. Both must stay in sync so
              Google's rich-result eligibility check passes (the question
              and answer in the structured data must match what users
              can read on the page). ===== */}
      {Array.isArray(data.faqs) && data.faqs.length > 0 && (
        <section
          className="relative isolate bg-white border-b border-[#E2E8F0]"
          data-testid={`service-detail-${slug}-faq`}
        >
          <PageSideAura testId="service-detail-faq-aura" />
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <SectionEyebrow accent={accent}>
              {isJa ? "よくあるご質問" : "Frequently asked questions"}
            </SectionEyebrow>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-tight mt-3 mb-6",
              ].join(" ")}
            >
              {isJa
                ? `${data.domain}についてよくいただくご質問`
                : `Common questions about ${data.domain}`}
            </h2>
            <ul className="divide-y divide-[#E2E8F0] border border-[#E2E8F0] rounded-2xl overflow-hidden">
              {data.faqs.map((f, i) => (
                <li key={i}>
                  <details
                    className="group bg-white"
                    data-testid={`service-detail-${slug}-faq-item-${i}`}
                  >
                    <summary
                      className="flex items-start justify-between gap-4 px-5 md:px-6 py-4 md:py-5 cursor-pointer list-none transition-colors hover:bg-[#F8FAFC]"
                      style={{ outlineColor: accent }}
                    >
                      <span
                        className={[
                          isJa ? "font-ja" : "font-display",
                          "text-[#0F1D2D] text-[15px] md:text-[16px] font-bold tracking-tight leading-snug pr-2",
                        ].join(" ")}
                      >
                        {pickLang(f.q, lang)}
                      </span>
                      <ChevronDown
                        className="flex-shrink-0 mt-0.5 h-5 w-5 text-[#475569] transition-transform duration-200 group-open:rotate-180"
                        strokeWidth={2}
                        aria-hidden
                      />
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 text-[14px] md:text-[15px] leading-[1.75] text-[#475569]">
                      {pickLang(f.a, lang)}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ===== SUPPORT CONTACT ===== */}
      {data.support?.email && (
        <section className="relative isolate bg-white border-b border-[#E2E8F0]" data-testid={`service-detail-${slug}-support`}>
          <PageSideAura testId="service-detail-support-aura" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div
              className="relative rounded-2xl overflow-hidden p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
              style={{ backgroundColor: soft }}
            >
              <BrandGraphic
                variant="wave"
                color={accent}
                className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none"
              />
              <div className="relative md:col-span-7">
                <SectionEyebrow accent={accent}>
                  {isJa ? "サポート窓口" : "Support contact"}
                </SectionEyebrow>
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa
                    ? `${data.domain} のサポートチームへ直接ご連絡いただけます。`
                    : `Reach the ${data.domain} support team directly.`}
                </h2>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-[#475569]">
                  {isJa ? data.support.hoursJa : data.support.hoursEn}
                </p>
              </div>
              <div className="relative md:col-span-5 flex flex-col gap-3">
                <a
                  href={`mailto:${data.support.email}`}
                  data-testid={`service-detail-${slug}-support-email`}
                  className="inline-flex items-center justify-between gap-3 px-5 py-4 rounded-xl bg-white border border-[#E2E8F0] hover:border-transparent hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.22)] transition-all group"
                >
                  <span className="inline-flex items-center gap-3">
                    <Mail className="h-5 w-5 shrink-0" style={{ color: accent }} strokeWidth={2.25} />
                    <span>
                      <span className="block text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#64748B]">
                        {isJa ? "メールでのお問い合わせ" : "Email"}
                      </span>
                      <span className="block text-[15px] font-bold text-[#0F1D2D]">
                        {data.support.email}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-[#94A3B8] group-hover:text-[#0F1D2D] transition-colors" strokeWidth={2.25} />
                </a>
                <Link
                  to={`/${lang}/contact`}
                  data-testid={`service-detail-${slug}-support-corporate`}
                  className="inline-flex items-center justify-between gap-3 px-5 py-4 rounded-xl bg-[#0E5FB0] text-white hover:bg-[#084785] transition-colors group"
                >
                  <span className="inline-flex items-center gap-3">
                    <Users className="h-5 w-5 shrink-0" strokeWidth={2.25} />
                    <span>
                      <span className="block text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold text-white/60">
                        {isJa ? "NoJo Enterprise 窓口" : "NoJo Enterprise desk"}
                      </span>
                      <span className="block text-[15px] font-bold">
                        {isJa ? "コーポレートへお問い合わせ" : "Contact our corporate desk"}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity" strokeWidth={2.25} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== CUSTOMER VOICES GRID (service-specific) ===== */}
      {slug === "japanetccard" && (
        <TestimonialRotator
          lang={lang}
          mode="grid"
          accent={accent}
          softBg={data.accentSoft || "#F4EEF9"}
          eyebrowEn="Recent reviews"
          eyebrowJa="最近のレビュー"
          headlineEn="Six recent voices from JapanETCcard customers."
          headlineJa="JapanETCcardをご利用のお客様からの最近の声。"
          linkTo={`/${lang}/testimonials`}
          serviceId="japanetccard"
          take={6}
          testIdSurface={`detail-${slug}-grid`}
        />
      )}
      {slug === "japantollreceipts" && (
        <TestimonialRotator
          lang={lang}
          mode="grid"
          accent={accent}
          softBg={data.accentSoft || "#EFF8FF"}
          eyebrowEn="Recent reviews"
          eyebrowJa="最近のレビュー"
          headlineEn="Six recent voices from JapanTollReceipts customers."
          headlineJa="JapanTollReceiptsをご利用のお客様からの最近の声。"
          linkTo={`/${lang}/testimonials`}
          serviceId="japantollreceipts"
          take={6}
          testIdSurface={`detail-${slug}-grid`}
        />
      )}

      {/* ===== INDEPENDENT-SERVICE DISCLAIMER ===== */}
      <section className="bg-[#5B2E83] text-white" data-testid={`service-detail-${slug}-disclaimer`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-1 flex lg:justify-center">
            <ShieldCheck className="h-10 w-10 text-[#7AB8E8]" strokeWidth={1.5} />
          </div>
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#94A3B8] uppercase font-bold mb-2">
              {isJa ? "独立したサービスです" : "Independent service"}
            </div>
            <p className="text-[14px] leading-[1.75] text-[#CBD5E1]">
              {data.note[lang]}
            </p>
          </div>
          <div className="lg:col-span-3 flex lg:justify-end">
            <Link
              to={`/${lang}/services`}
              data-testid={`service-detail-${slug}-back-services`}
              className="inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-3 rounded-full text-[13px] font-semibold hover:bg-[#F1F5F9] transition-colors"
            >
              {isJa ? "他のサービスを見る" : "Explore all services"}
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== BOTTOM JUMP NAV — same destinations as the top bar ===== */}
      <ServiceJumpNav activeSlug={slug} lang={lang} variant="bottom" />
    </article>
  );
}
